import { TestStorageService, FlagStorageService, RemoteConfigStorageService } from './chunk-PEEG2VDJ.mjs';
import { __spreadProps, __spreadValues } from './chunk-AQQGP23I.mjs';
import { Abby, HttpService, AbbyEventType } from '@tryabby/core';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { jsx } from 'react/jsx-runtime';

function createAbby(abbyConfig) {
  const abby = new Abby(
    abbyConfig,
    {
      get: (key) => {
        if (typeof window === "undefined")
          return null;
        return TestStorageService.get(abbyConfig.projectId, key);
      },
      set: (key, value) => {
        var _a;
        if (typeof window === "undefined" || ((_a = config.cookies) == null ? void 0 : _a.disableByDefault))
          return;
        TestStorageService.set(abbyConfig.projectId, key, value);
      }
    },
    {
      get: (key) => {
        if (typeof window === "undefined")
          return null;
        return FlagStorageService.get(abbyConfig.projectId, key);
      },
      set: (key, value) => {
        if (typeof window === "undefined")
          return;
        FlagStorageService.set(abbyConfig.projectId, key, value);
      }
    },
    {
      get: (key) => {
        if (typeof window === "undefined")
          return null;
        return RemoteConfigStorageService.get(abbyConfig.projectId, key);
      },
      set: (key, value) => {
        if (typeof window === "undefined")
          return;
        RemoteConfigStorageService.set(abbyConfig.projectId, key, value);
      }
    }
  );
  const AbbyContext = React.createContext(null);
  const useAbbyData = () => {
    const data = React.useContext(AbbyContext);
    if (!data) {
      throw new Error(
        "useAbbyData must be used within an AbbyProvider. Wrap a parent component in <AbbyProvider> to fix this error."
      );
    }
    return data;
  };
  const config = abbyConfig;
  const useAbby = (name, lookupObject) => {
    var _a;
    const { tests } = useAbbyData();
    const [selectedVariant, setSelectedVariant] = useState("");
    useEffect(() => {
      var _a2;
      const newVariant = (_a2 = tests[name]) == null ? void 0 : _a2.selectedVariant;
      if (newVariant !== void 0) {
        setSelectedVariant(newVariant);
      }
    }, [(_a = tests[name]) == null ? void 0 : _a.selectedVariant]);
    useEffect(() => {
      var _a2, _b;
      setSelectedVariant(
        (_b = (_a2 = abby.getProjectData().tests[name]) == null ? void 0 : _a2.selectedVariant) != null ? _b : ""
      );
    }, [name]);
    useEffect(() => {
      if (!name || !selectedVariant)
        return;
      HttpService.sendData({
        url: config.apiUrl,
        type: AbbyEventType.PING,
        data: {
          projectId: config.projectId,
          selectedVariant,
          testName: name
        }
      });
    }, [name, selectedVariant]);
    const onAct = useCallback(() => {
      if (!selectedVariant)
        return;
      HttpService.sendData({
        url: config.apiUrl,
        type: AbbyEventType.ACT,
        data: {
          projectId: config.projectId,
          selectedVariant,
          testName: name
        }
      });
    }, [name, selectedVariant]);
    return {
      /**
       * This function can be called to indicate that something that
       * uses the selected variant has been rendered.
       * It will automatically send the selected variant to the server.
       */
      onAct,
      variant: lookupObject ? lookupObject[selectedVariant] : (
        // Typescript fails here. If we cast selectedVariant to TestVariant
        // it still assumes that it is a string. So we cast it to any instead
        selectedVariant
      )
    };
  };
  const useFeatureFlag = (name) => {
    const data = useAbbyData();
    return data.flags[name];
  };
  const useFeatureFlags = () => {
    const data = useAbbyData();
    return Object.keys(data.flags).map((flagName) => ({
      name: flagName,
      value: data.flags[flagName]
    }));
  };
  const useRemoteConfigVariables = () => {
    const data = useAbbyData();
    return Object.keys(data.remoteConfig).map((configName) => ({
      name: configName,
      value: data.remoteConfig[configName]
    }));
  };
  const AbbyProvider = ({
    children,
    initialData
  }) => {
    const isMountedRef = useRef(false);
    const [data, setData] = useState(() => {
      if (initialData) {
        return abby.init(initialData);
      }
      return abby.getProjectData();
    });
    useEffect(() => {
      if (initialData || isMountedRef.current)
        return;
      isMountedRef.current = true;
      abby.loadProjectData().then((data2) => {
        if (!data2)
          return;
        setData(data2);
      });
    }, []);
    useEffect(() => {
      const unsubscribe = abby.subscribe((data2) => {
        setData(data2);
      });
      return unsubscribe;
    }, []);
    return /* @__PURE__ */ jsx(AbbyContext.Provider, { value: data, children });
  };
  const getFeatureFlagValue = (name) => {
    return abby.getFeatureFlag(name);
  };
  const useRemoteConfig = (remoteConfigName) => {
    const abby2 = useAbbyData();
    return abby2.remoteConfig[remoteConfigName];
  };
  const getRemoteConfig = (remoteConfigName) => {
    return abby.getRemoteConfig(remoteConfigName);
  };
  const getABTestValue = (testName, lookupObject) => {
    const variant = abby.getTestVariant(testName);
    if (lookupObject === void 0) {
      return variant;
    }
    return lookupObject[variant];
  };
  const withDevtools = (factory, props) => {
    return () => {
      const initedRef = useRef(false);
      useEffect(() => {
        if (initedRef.current) {
          return;
        }
        if (!(props == null ? void 0 : props.dangerouslyForceShow) && process.env.NODE_ENV !== "development") {
          return;
        }
        initedRef.current = true;
        const destroy = factory.create(__spreadProps(__spreadValues({}, props), { abby }));
        return () => {
          initedRef.current = false;
          destroy();
        };
      }, []);
      return null;
    };
  };
  const getABResetFunction = (name) => {
    return () => {
      TestStorageService.remove(config.projectId, name);
    };
  };
  const getVariants = (name) => {
    return abby.getVariants(name);
  };
  return {
    useAbby,
    AbbyProvider,
    useFeatureFlag,
    getFeatureFlagValue,
    useRemoteConfig,
    getRemoteConfig,
    getABTestValue,
    __abby__: abby,
    withDevtools,
    getABResetFunction,
    getVariants,
    useFeatureFlags,
    useRemoteConfigVariables
  };
}

export { createAbby };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=chunk-4XFILT7X.mjs.map